<template>
  <div class="space-y-6">
    <div
      v-if="!loading && !hasSyncedReviews"
      class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Review data has not been connected yet
          </h3>
          <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Add or confirm the Place ID or LocalViking location identifier before using reviews in local analysis. Once the location is connected, use Sync to import the first review set.
          </p>
        </div>
        <button
          @click="syncReviews"
          :disabled="syncing"
          class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
          <svg v-if="syncing" class="animate-spin -ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Sync
        </button>
      </div>
    </div>

    <template v-else>
      <!-- Stat Cards Row -->
      <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Average Rating
          </dt>
          <dd class="mt-1 flex items-baseline gap-2">
            <span class="text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ stats.average_rating || '—' }}</span>
            <div class="flex items-center gap-0.5">
              <svg v-for="n in 5" :key="n" class="h-3.5 w-3.5"
                :class="n <= Math.round(stats.average_rating || 0) ? 'text-amber-400' : 'text-gray-300 dark:text-gray-600'"
                fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            </div>
          </dd>
        </div>
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Total Reviews
          </dt>
          <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {{ stats.total_count || 0 }}
          </dd>
        </div>
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Reply Rate
          </dt>
          <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {{ replyRate }}%
          </dd>
          <div class="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {{ stats.reply_count || 0 }} replied
          </div>
        </div>
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
          <dt class="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            With Text
          </dt>
          <dd class="mt-1 text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {{ textRate }}%
          </dd>
          <div class="mt-1 text-xs text-gray-400 dark:text-gray-500">
            {{ stats.with_text_count || 0 }} of {{ stats.total_count || 0 }}
          </div>
        </div>
      </div>

      <!-- Rating Distribution + Review Timeline -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <!-- Rating Distribution -->
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Rating Distribution
          </h3>
          <div ref="ratingChart" class="w-full" style="height: 200px;" />
        </div>

        <!-- Review Timeline -->
        <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Reviews Over Time
          </h3>
          <div ref="timelineChart" class="w-full relative" style="height: 200px;" />
        </div>
      </div>

      <!-- Filters + Sync Row -->
      <div class="flex items-center justify-between">
        <div class="flex flex-wrap items-center gap-2">
          <button
            @click="selectedRating = null; currentPage = 1; fetchReviews()"
            :class="selectedRating === null
              ? 'bg-primary-500 text-white'
              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'"
            class="px-3 py-1.5 text-sm font-medium rounded-lg">
            All
          </button>
          <button
            v-for="n in [5, 4, 3, 2, 1]"
            :key="n"
            @click="selectedRating = n; currentPage = 1; fetchReviews()"
            :class="selectedRating === n
              ? 'bg-primary-500 text-white'
              : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'"
            class="px-3 py-1.5 text-sm font-medium rounded-lg">
            {{ n }} ★
          </button>
          <label class="ml-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input type="checkbox" v-model="textOnly" @change="currentPage = 1; fetchReviews()" class="rounded border-gray-300 dark:border-gray-600 text-primary-500 focus:ring-primary-500">
            With text only
          </label>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="stats.reviews_synced_at" class="text-xs text-gray-500 dark:text-gray-400">
            Last synced {{ formatDate(stats.reviews_synced_at) }}
          </span>
          <button
            @click="syncReviews"
            :disabled="syncing"
            class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
            <svg v-if="syncing" class="animate-spin -ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Sync
          </button>
        </div>
      </div>

      <!-- Results count -->
      <div v-if="pagination.total" class="text-xs text-gray-500 dark:text-gray-400">
        Showing {{ (pagination.page - 1) * pagination.per_page + 1 }}–{{ Math.min(pagination.page * pagination.per_page, pagination.total) }} of {{ pagination.total }} reviews
      </div>

      <!-- Reviews List -->
      <div class="space-y-3">
        <div v-for="review in reviews" :key="review.id"
          class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 transition-colors hover:border-gray-300 dark:hover:border-gray-600">
          <div class="flex items-start gap-3">
            <div v-if="review.reviewer_photo_url"
              class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-gray-100 dark:ring-gray-700">
              <img :src="review.reviewer_photo_url" :alt="review.reviewer_name" class="w-full h-full object-cover">
            </div>
            <div v-else
              class="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center flex-shrink-0 ring-2 ring-gray-100 dark:ring-gray-700">
              <span class="text-sm font-medium text-primary-700 dark:text-primary-300">
                {{ initials(review.reviewer_name) }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{ review.reviewer_name || 'Anonymous' }}
                  </span>
                  <div class="flex items-center gap-0.5">
                    <svg v-for="n in 5" :key="n" class="h-3.5 w-3.5"
                      :class="n <= review.rating ? 'text-amber-400' : 'text-gray-200 dark:text-gray-700'"
                      fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                </div>
                <span class="text-xs text-gray-400 dark:text-gray-500">
                  {{ timeAgo(review.remote_created_at) }}
                </span>
              </div>
              <p v-if="review.text" class="mt-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                {{ review.text }}
              </p>
              <p v-else class="mt-2 text-xs text-gray-400 dark:text-gray-500 italic">
                No review text
              </p>
              <!-- Reply -->
              <div v-if="review.reply" class="mt-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
                <div class="flex items-center gap-1.5 mb-1">
                  <svg class="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Business reply</span>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                  {{ review.reply }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!loading && reviews.length === 0"
          class="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          <svg class="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
          </svg>
          No reviews found for the selected filters.
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.total_pages > 1" class="flex items-center justify-between pt-2">
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage <= 1"
          class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed">
          Previous
        </button>
        <div class="flex items-center gap-1">
          <button
            v-for="p in visiblePages"
            :key="p"
            @click="p !== '...' && goToPage(p)"
            :disabled="p === '...'"
            :class="p === currentPage
              ? 'bg-primary-500 text-white border-primary-500'
              : p === '...' ? 'cursor-default border-transparent text-gray-400' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'"
            class="w-8 h-8 flex items-center justify-center border text-sm font-medium rounded-lg">
            {{ p }}
          </button>
        </div>
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage >= pagination.total_pages"
          class="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed">
          Next
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import * as d3 from 'd3'
import { getLocationReviews, syncLocationReviews } from '~/javascript/api/location_reviews.js'

const props = defineProps({
  locationId: { type: String, required: true }
})

const reviews = ref([])
const stats = ref({})
const pagination = ref({})
const loading = ref(false)
const syncing = ref(false)
const selectedRating = ref(null)
const textOnly = ref(false)
const currentPage = ref(1)

const ratingChart = ref(null)
const timelineChart = ref(null)

const hasSyncedReviews = computed(() => Boolean(stats.value.reviews_synced_at))

const replyRate = computed(() => {
  const total = stats.value.total_count || 0
  if (total === 0) return 0
  return Math.round((stats.value.reply_count || 0) / total * 100)
})

const textRate = computed(() => {
  const total = stats.value.total_count || 0
  if (total === 0) return 0
  return Math.round((stats.value.with_text_count || 0) / total * 100)
})

const visiblePages = computed(() => {
  const total = pagination.value.total_pages || 1
  const current = currentPage.value
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages = [1]
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i += 1) {
    pages.push(i)
  }
  if (current < total - 2) pages.push('...')
  if (total > 1) pages.push(total)
  return pages
})

const ratingCount = (n) => stats.value.rating_distribution?.[n] || 0

const initials = (name) => {
  if (!name) return '?'
  return name.split(' ').map((w) => w[0]).join('').toUpperCase()
    .slice(0, 2)
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const timeAgo = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
  return formatDate(dateStr)
}

const RATING_COLORS = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e']
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const renderRatingChart = () => {
  const container = ratingChart.value
  if (!container || !stats.value.rating_distribution) return

  d3.select(container).selectAll('*').remove()

  const data = [5, 4, 3, 2, 1].map((n) => ({
    rating: n,
    count: ratingCount(n),
    color: RATING_COLORS[n - 1]
  }))

  const total = stats.value.total_count || 1
  const margin = { top: 5, right: 50, bottom: 5, left: 30 }
  const width = container.clientWidth - margin.left - margin.right
  const height = 200 - margin.top - margin.bottom
  const barHeight = 28
  const barGap = (height - barHeight * 5) / 4

  const svg = d3.select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  const xScale = d3.scaleLinear().domain([0, d3.max(data, (d) => d.count) || 1]).range([0, width])

  const groups = svg.selectAll('.bar-group')
    .data(data)
    .join('g')
    .attr('class', 'bar-group')
    .attr('transform', (d, i) => `translate(0, ${i * (barHeight + barGap)})`)

  // Star label
  groups.append('text')
    .attr('x', -5)
    .attr('y', barHeight / 2)
    .attr('dy', '0.35em')
    .attr('text-anchor', 'end')
    .attr('fill', '#6b7280')
    .attr('font-size', '12px')
    .text((d) => `${d.rating}`)

  // Star icon
  groups.append('text')
    .attr('x', -2)
    .attr('y', barHeight / 2)
    .attr('dy', '0.35em')
    .attr('text-anchor', 'start')
    .attr('fill', '#fbbf24')
    .attr('font-size', '10px')
    .text('★')

  // Background bar
  groups.append('rect')
    .attr('x', 12)
    .attr('y', 0)
    .attr('width', width - 12)
    .attr('height', barHeight)
    .attr('rx', 6)
    .attr('fill', '#f3f4f6')

  // Value bar with animation
  groups.append('rect')
    .attr('x', 12)
    .attr('y', 0)
    .attr('width', 0)
    .attr('height', barHeight)
    .attr('rx', 6)
    .attr('fill', (d) => d.color)
    .attr('fill-opacity', 0.8)
    .transition()
    .duration(600)
    .delay((d, i) => i * 80)
    .attr('width', (d) => Math.max(0, xScale(d.count) - 12))

  // Count + percentage label
  groups.append('text')
    .attr('x', width + 5)
    .attr('y', barHeight / 2)
    .attr('dy', '0.35em')
    .attr('fill', '#374151')
    .attr('font-size', '12px')
    .attr('font-weight', '500')
    .text((d) => `${d.count} (${Math.round(d.count / total * 100)}%)`)
}

const renderTimelineChart = () => {
  const container = timelineChart.value
  if (!container || reviews.value.length === 0) return

  d3.select(container).selectAll('*').remove()

  const reviewsByMonth = {}
  reviews.value.forEach((r) => {
    if (!r.remote_created_at) return
    const d = new Date(r.remote_created_at)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    reviewsByMonth[key] = (reviewsByMonth[key] || 0) + 1
  })

  // Generate last 12 months
  const monthBuckets = []
  const now = new Date()
  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    monthBuckets.push({ date: d, key, count: reviewsByMonth[key] || 0 })
  }

  const margin = { top: 10, right: 10, bottom: 30, left: 35 }
  const width = container.clientWidth - margin.left - margin.right
  const height = 200 - margin.top - margin.bottom

  const svg = d3.select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`)

  const xScale = d3.scaleBand()
    .domain(monthBuckets.map((m) => m.key))
    .range([0, width])
    .padding(0.3)

  const yScale = d3.scaleLinear()
    .domain([0, Math.max(d3.max(monthBuckets, (m) => m.count), 1)])
    .nice()
    .range([height, 0])

  // Grid
  svg.append('g')
    .call(d3.axisLeft(yScale).tickSize(-width).tickFormat('').ticks(4))
    .selectAll('line')
    .attr('stroke', '#e5e7eb')
    .attr('stroke-dasharray', '3,3')
  svg.selectAll('.domain').remove()

  // Bars with animation
  svg.selectAll('.bar')
    .data(monthBuckets)
    .join('rect')
    .attr('class', 'bar')
    .attr('x', (d) => xScale(d.key))
    .attr('y', height)
    .attr('width', xScale.bandwidth())
    .attr('height', 0)
    .attr('rx', 4)
    .attr('fill', '#304f95')
    .attr('fill-opacity', 0.7)
    .style('cursor', 'pointer')
    // eslint-disable-next-line func-names
    .on('mouseenter', function() { d3.select(this).attr('fill-opacity', 1) })
    // eslint-disable-next-line func-names
    .on('mouseleave', function() { d3.select(this).attr('fill-opacity', 0.7) })
    .transition()
    .duration(500)
    .delay((d, i) => i * 40)
    .attr('y', (d) => yScale(d.count))
    .attr('height', (d) => height - yScale(d.count))

  // Count labels on bars
  svg.selectAll('.label')
    .data(monthBuckets.filter((m) => m.count > 0))
    .join('text')
    .attr('class', 'label')
    .attr('x', (d) => xScale(d.key) + xScale.bandwidth() / 2)
    .attr('y', (d) => yScale(d.count) - 4)
    .attr('text-anchor', 'middle')
    .attr('fill', '#374151')
    .attr('font-size', '10px')
    .attr('font-weight', '500')
    .text((d) => d.count)

  // X axis
  svg.append('g')
    .attr('transform', `translate(0,${height})`)
    .call(d3.axisBottom(xScale).tickFormat((k) => {
      const parts = k.split('-')
      return MONTH_NAMES[parseInt(parts[1], 10) - 1]
    }))
    .selectAll('text')
    .attr('fill', '#6b7280')
    .attr('font-size', '10px')

  // Y axis
  svg.append('g')
    .call(d3.axisLeft(yScale).ticks(4).tickFormat(d3.format('d')))
    .selectAll('text')
    .attr('fill', '#6b7280')
    .attr('font-size', '10px')

  svg.selectAll('.tick line').attr('stroke', '#e5e7eb')
}

const renderCharts = () => {
  renderRatingChart()
  renderTimelineChart()
}

const fetchReviews = async() => {
  loading.value = true
  try {
    const params = { page: currentPage.value, per_page: 15 }
    if (selectedRating.value) params.rating = selectedRating.value
    if (textOnly.value) params.with_text = 'true'

    const response = await getLocationReviews(props.locationId, params)
    reviews.value = response.data.reviews || []
    stats.value = response.data.stats || {}
    pagination.value = response.data.pagination || {}
    await nextTick()
    renderCharts()
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error fetching reviews:', err)
  } finally {
    loading.value = false
  }
}

const goToPage = (page) => {
  if (page < 1 || page > pagination.value.total_pages) return
  currentPage.value = page
  fetchReviews()
  document.querySelector('[data-reviews-list]')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

const syncReviews = async() => {
  syncing.value = true
  try {
    await syncLocationReviews(props.locationId)
    setTimeout(() => {
      fetchReviews()
      syncing.value = false
    }, 5000)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error syncing reviews:', err)
    syncing.value = false
  }
}

onMounted(fetchReviews)
</script>
